import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Shield, CheckCircle2, FileText, Lock, 
  AlertTriangle, Clock, Eye, Trash2, Globe
} from "lucide-react";
import { TrustNavigation } from "@/components/TrustNavigation";
import { TrustFooter } from "@/components/TrustFooter";

export default function ComplianceDataHandling() {

  const dataCategories = [
    {
      category: "Candidate Personal Data",
      items: ["Name, email, phone", "Location information", "Work history", "Education details"],
      retention: "24 months from last activity",
      legal: "Consent-based collection"
    },
    {
      category: "Assessment Recordings",
      items: ["Video recordings", "Audio transcripts", "Response timestamps"],
      retention: "12 months from assessment date",
      legal: "Explicit consent at assessment start"
    },
    {
      category: "Assessment Data",
      items: ["Scoring outputs", "Verification results", "Agent notes"],
      retention: "24 months from assessment",
      legal: "Legitimate interest for service delivery"
    },
    {
      category: "Employer Data",
      items: ["Contact information", "Role requirements", "Feedback on candidates"],
      retention: "Duration of service agreement + 24 months",
      legal: "Contractual necessity"
    }
  ];

  const privacyRights = [
    {
      right: "Access",
      description: "Request a copy of personal data held about you"
    },
    {
      right: "Correction",
      description: "Request correction of inaccurate personal data"
    },
    {
      right: "Deletion",
      description: "Request deletion of personal data where applicable"
    },
    {
      right: "Objection",
      description: "Object to processing for specific purposes"
    },
    {
      right: "Portability",
      description: "Request data in a portable format"
    },
    {
      right: "Withdraw Consent",
      description: "Withdraw previously given consent at any time"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TrustNavigation currentPath="/compliance-data-handling" />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950" />
        <div className="container mx-auto px-4 py-10 md:py-24 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium border border-white/20 mb-6">
              <Shield className="w-4 h-4" />
              <span>Compliance & Data Handling</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Data Protection & Privacy
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              How we collect, process, store, and protect personal data in compliance with applicable privacy legislation.
            </p>
            <p className="text-sm text-slate-400 mt-4">
              Last updated: January 2026 | Version 1.3
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 bg-slate-50 border-slate-200 shadow-sm mb-12">
              <div className="flex items-start gap-4">
                <Globe className="w-8 h-8 text-slate-700 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg mb-2">Regulatory Framework</h3>
                  <p className="text-slate-700 mb-4">
                    VEXIS processes personal data in accordance with the New Zealand Privacy Act 2020 and follows 
                    principles aligned with international privacy standards. Our data handling practices are designed 
                    to meet the requirements of major privacy frameworks.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-slate-200 text-slate-700 px-3 py-1 rounded-full font-medium">NZ Privacy Act 2020</span>
                    <span className="text-xs bg-slate-200 text-slate-700 px-3 py-1 rounded-full font-medium">Privacy Principles</span>
                    <span className="text-xs bg-slate-200 text-slate-700 px-3 py-1 rounded-full font-medium">Data Minimization</span>
                  </div>
                </div>
              </div>
            </Card>

            <h2 className="text-2xl font-bold text-slate-900 mb-6">Data Categories & Retention</h2>
            <div className="space-y-4 mb-12">
              {dataCategories.map((cat, idx) => (
                <Card key={idx} className="p-6 bg-white border-slate-200 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-3">{cat.category}</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Data Types</p>
                      <ul className="text-sm text-slate-600 space-y-1">
                        {cat.items.map((item, iIdx) => (
                          <li key={iIdx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Retention Period</p>
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {cat.retention}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Legal Basis</p>
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <FileText className="w-4 h-4 text-slate-400" />
                        {cat.legal}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mb-6">Your Privacy Rights</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {privacyRights.map((right, idx) => (
                <Card key={idx} className="p-4 bg-white border-slate-200 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-1">{right.right}</h3>
                  <p className="text-sm text-slate-600">{right.description}</p>
                </Card>
              ))}
            </div>

            <Card className="p-6 bg-slate-50 border-slate-200 shadow-sm">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-slate-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Data Subject Requests</h3>
                  <p className="text-slate-700 text-sm mb-3">
                    To exercise any of your privacy rights, please contact us via the enquiry form or email. 
                    We will respond to valid requests within the timeframes required by applicable law (typically 20 working days under NZ Privacy Act 2020).
                  </p>
                  <Link href="/contact">
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">
                      Submit Privacy Request
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Security Measures</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 bg-white border-slate-200 shadow-sm">
                <Lock className="w-8 h-8 text-slate-700 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Technical Controls</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>Encrypted data transmission (TLS)</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>Encrypted data storage</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>Access logging and monitoring</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>Regular security assessments</li>
                </ul>
              </Card>
              <Card className="p-6 bg-white border-slate-200 shadow-sm">
                <Eye className="w-8 h-8 text-slate-700 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Organizational Controls</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>Role-based access restrictions</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>Data handling procedures</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>Incident response processes</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>Regular policy reviews</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <TrustFooter />
    </div>
  );
}
